import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { getApi, postApi } from '../../../Services/apiService';
import { CHECKR_STATUS, CHECKR_PACKAGE, getCheckrStatusLabel, canStartBackgroundCheck } from '../../../Utils/checkrStatus';
import { normalizeDocumentUrls } from '../../../Utils/tenantDocuments';
import { toast } from 'react-toastify';
import FormattedPhone from '../../../Components/FormattedPhone/FormattedPhone';
import LeaseAgreementCard from './LeaseAgreementCard';

const CLEARDIL_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/** GET verification-result only when approvals exist and every row is still NOT_STARTED on ClearDIL. */
function shouldFetchCleardilVerification(approvals) {
  if (!Array.isArray(approvals) || approvals.length === 0) return false;
  return approvals.every((item) => item?.cleardil_status === CLEARDIL_STATUS.NOT_STARTED);
}

function getReferenceCheckField(data, ...keys) {
  if (!data) return null;
  for (const key of keys) {
    const value = data[key];
    if (value != null && value !== '') return value;
  }
  return null;
}

function formatReferenceCheckTimestamp(value) {
  if (value == null || value === '') return '';
  const str = String(value);
  const asNum = Number(str);
  const date = !Number.isNaN(asNum) && /^\d+$/.test(str) ? new Date(asNum) : new Date(str);
  if (Number.isNaN(date.getTime())) return str;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Property_Reference_Check.status
const REFERENCE_CHECK_STATUS = {
  NOT_REQUESTED: '0',
  SENT: '1',
  ACCEPTED: '2',
  REJECTED: '3',
};

const CHECKR_ESSENTIAL_FEATURES = [
  'Criminal history',
  'Credit report',
  'Eviction history',
  'Sex offender registry',
  'Global watchlist',
];

const CHECKR_PACKAGE_OPTIONS = [
  {
    value: CHECKR_PACKAGE.ESSENTIAL,
    title: 'Essential',
    price: '$39.99',
    features: CHECKR_ESSENTIAL_FEATURES,
  },
  {
    value: CHECKR_PACKAGE.COMPLETE,
    title: 'Complete',
    price: '$49.99',
    features: [...CHECKR_ESSENTIAL_FEATURES, 'Income verification (bank link or income documents)'],
  },
];

const REFERENCE_CHECK_STATUS_LABELS = {
  [REFERENCE_CHECK_STATUS.NOT_REQUESTED]: 'Not Requested',
  [REFERENCE_CHECK_STATUS.SENT]: 'Sent',
  [REFERENCE_CHECK_STATUS.ACCEPTED]: 'Accepted',
  [REFERENCE_CHECK_STATUS.REJECTED]: 'Rejected',
};

const REFERENCE_CHECK_STATUS_VARIANT = {
  [REFERENCE_CHECK_STATUS.NOT_REQUESTED]: 'not-requested',
  [REFERENCE_CHECK_STATUS.SENT]: 'sent',
  [REFERENCE_CHECK_STATUS.ACCEPTED]: 'accepted',
  [REFERENCE_CHECK_STATUS.REJECTED]: 'rejected',
};

function getReferenceCheckStatusInfo(status) {
  const key = status == null ? REFERENCE_CHECK_STATUS.NOT_REQUESTED : String(status);
  return {
    key,
    label: REFERENCE_CHECK_STATUS_LABELS[key] ?? 'Not Requested',
    variant: REFERENCE_CHECK_STATUS_VARIANT[key] ?? 'not-requested',
  };
}

function buildReferenceCheckDetails(data) {
  if (!data || typeof data !== 'object') return null;

  const statusInfo = getReferenceCheckStatusInfo(data.status);
  const rows = [];

  const referencePhone = getReferenceCheckField(data, 'reference_phone', 'referencePhone');
  if (referencePhone) {
    rows.push({ id: 'phone', label: 'Reference phone', value: String(referencePhone), isPhone: true });
  }

  const sentAt = formatReferenceCheckTimestamp(
    getReferenceCheckField(data, 'sent_at', 'sentAt', 'created_at', 'createdAt')
  );
  const updatedAt = formatReferenceCheckTimestamp(
    getReferenceCheckField(data, 'completed_at', 'completedAt', 'updated_at', 'updatedAt')
  );

  if (sentAt) {
    rows.push({ id: 'sent', label: 'SMS sent on', value: sentAt });
  }
  if (updatedAt && updatedAt !== sentAt) {
    rows.push({ id: 'updated', label: 'Last updated', value: updatedAt });
  }

  const smsResponse = getReferenceCheckField(
    data,
    'sms_response',
    'sms_reply',
    'smsResponse',
    'landlord_reply',
    'response',
    'structured_response'
  );

  return { statusInfo, rows, smsResponse: smsResponse ? String(smsResponse) : null };
}

function canSendReferenceCheck(referenceCheck) {
  if (!referenceCheck) return true;
  return String(referenceCheck.status ?? REFERENCE_CHECK_STATUS.NOT_REQUESTED) === REFERENCE_CHECK_STATUS.NOT_REQUESTED;
}

const TenantDetail = () => {
  const { id, tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentViewIndex, setDocumentViewIndex] = useState(0);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineReasonError, setDeclineReasonError] = useState('');
  const [declineContext, setDeclineContext] = useState(null); // { type: 'application' } | { type: 'document', doc }
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const [isAutoScreening, setIsAutoScreening] = useState(false);
  const [referenceCheck, setReferenceCheck] = useState(null);
  const [isReferenceCheckLoading, setIsReferenceCheckLoading] = useState(false);
  const [isVerifyingReference, setIsVerifyingReference] = useState(false);
  const [isRequestingBackgroundCheck, setIsRequestingBackgroundCheck] = useState(false);
  const [backgroundCheckModalOpen, setBackgroundCheckModalOpen] = useState(false);
  const [backgroundCheckPackage, setBackgroundCheckPackage] = useState(CHECKR_PACKAGE.ESSENTIAL);

  const fetchVerificationResult = useCallback(async (propertyId, tenantDocumentId, { silent = false } = {}) => {
    if (!propertyId || !tenantDocumentId) return null;
    try {
      const response = await getApi('/verification/tenant/verification-result', {
        params: { property_id: propertyId, tenant_document_id: tenantDocumentId },
      });
      if (response?.statusCode === 200) {
        return response?.data ?? response;
      }
      if (!silent) {
        toast.error(response?.message || 'Failed to fetch verification results.');
      }
      return null;
    } catch (error) {
      console.error('Error fetching verification result:', error);
      if (!silent) {
        toast.error('Failed to fetch verification results.');
      }
      return null;
    }
  }, []);

  const fetchReferenceCheckStatus = useCallback(async (propertyId, tenantDocumentId, { silent = false } = {}) => {
    if (!propertyId || !tenantDocumentId) return null;
    if (!silent) setIsReferenceCheckLoading(true);
    try {
      const response = await getApi('/reference-check/status', {
        params: { property_id: propertyId, tenant_document_id: tenantDocumentId },
      });
      if (response?.statusCode === 200) {
        const row = response?.data ?? response;
        setReferenceCheck(row && typeof row === 'object' ? row : null);
        return row;
      }
      if (response?.statusCode === 404) {
        setReferenceCheck(null);
        return null;
      }
      if (!silent) {
        toast.error(response?.message || 'Failed to load reference check status.');
      }
      return null;
    } catch (error) {
      const statusCode = error?.response?.status ?? error?.response?.data?.statusCode;
      if (statusCode === 404) {
        setReferenceCheck(null);
        return null;
      }
      console.error('Error fetching reference check status:', error);
      if (!silent) {
        toast.error(error?.response?.data?.message || 'Failed to load reference check status.');
      }
      return null;
    } finally {
      if (!silent) {
        setIsReferenceCheckLoading(false);
      }
    }
  }, []);

  const fetchScreening = useCallback(async (silent = false) => {
    if (!tenantId) return null;
    if (!silent) setIsLoading(true);
    try {
        const response = await getApi(`/screening/${tenantId}`);
        if (response?.statusCode === 200 && response?.data) {
          const d = response.data;
          const prop = d.property || {};
          const addr = [prop.address, prop.unit_number].filter(Boolean).join(', ') || '—';
          const rawDoc = d.tenant_document ?? d.tenant_documents;
          const docList = Array.isArray(rawDoc)
            ? rawDoc
            : rawDoc && typeof rawDoc === 'object'
              ? [rawDoc]
              : [];
          const firstDoc = docList[0];
          const docStatus = firstDoc?.verification_status === '1';

          const getReviewStatusFromApprovals = (approvals, field_name) => {
            const approval = Array.isArray(approvals)
              ? approvals.find((a) => String(a?.field_name) === String(field_name))
              : null;
            const status = approval?.status != null ? String(approval.status) : null;
            if (status === '1') return 'approved';  // 1 = accepted/approved
            if (status === '2') return 'declined';  // 2 = declined
            return null;
          };

          const documents = [];
          docList.forEach((docItem) => {
            const verified = docItem?.verification_status === '1';
            const period = docItem?.months_covered ? `${docItem.months_covered} months` : '';
            const tenant_document_id = docItem.id;
            const approvals = docItem?.approvals;
            const payStubUrls = normalizeDocumentUrls(docItem?.pay_stub_url);
            if (payStubUrls.length > 0) {
              documents.push({
                type: 'Pay Stubs',
                period: period || '2 to 3 months',
                verified,
                imageUrl: payStubUrls[0],
                allUrls: payStubUrls,
                isPdf: /\.pdf$/i.test(payStubUrls[0]),
                tenant_document_id,
                field_name: 0,
                reviewStatus: getReviewStatusFromApprovals(approvals, 0),
              });
            }
            const bankUrls = normalizeDocumentUrls(docItem?.bank_statement);
            if (bankUrls.length > 0) {
              documents.push({
                type: 'Bank Statements',
                period: period || 'Last 2 to 3 months',
                verified,
                imageUrl: bankUrls[0],
                allUrls: bankUrls,
                isPdf: /\.pdf$/i.test(bankUrls[0]),
                tenant_document_id,
                field_name: 1,
                reviewStatus: getReviewStatusFromApprovals(approvals, 1),
              });
            }
          });

          const references = [];
          docList.forEach((docItem) => {
            if (docItem?.previous_landlord_name) {
              references.push({
                name: docItem.previous_landlord_name,
                address: docItem.previous_landlord_address || '',
                email: docItem.previous_landlord_email || '',
                phone: docItem.previous_landlord_phone || '',
              });
            }
          });

          const transcript = [];
          if (Array.isArray(d.screening_call)) {
            (d.screening_call || []).forEach((item) => {
              const question = item.question != null && String(item.question).trim() !== '' ? String(item.question).trim() : null;
              const answer = item.answer != null && String(item.answer).trim() !== '' ? String(item.answer).trim() : null;
              if (question) transcript.push({ speaker: 'System', message: question });
              if (answer) transcript.push({ speaker: 'Tenant', message: answer });
            });
          }

          const grade = d.grade || '—';
          const score = d.ai_score != null ? d.ai_score : null;
          const propertyTenantStatus = d.property_tenant_status != null ? String(d.property_tenant_status) : '0';
          const getRecommendation = () => {
            if (propertyTenantStatus === '1') return { title: 'Approved', description: 'Application approved' };
            if (propertyTenantStatus === '2') return { title: 'Declined', description: 'Application declined' };
            if (grade === 'A' || grade === 'A+') return { title: 'Excellent Candidate', description: 'Highly recommended for rental' };
            if (grade === 'B' || grade === 'B+') return { title: 'Good Candidate', description: 'Recommended for rental' };
            if (grade === 'C') return { title: 'Moderate Candidate', description: 'Review additional details' };
            return { title: 'Under Review', description: 'Screening in progress' };
          };
          const rec = getRecommendation();

          const propertyId = d.property_id || prop.id || id;
          const tenantDocumentId = docList[0]?.id ?? null;

          setTenant({
            id: d.id,
            propertyId,
            tenantDocumentId,
            tenantUserId: d.tenant?.id ?? d.tenant_id ?? null,
            propertyTenantStatus,
            isPropertyAssigned: d.is_property_assigned === true,
            backgroundCheckStatus: d.background_check_status ?? null,
            backgroundCheckPackage: d.background_check_package ?? null,
            firstName: d.tenant?.first_name || '',
            lastName: d.tenant?.last_name || '',
            email: d.tenant?.email || '—',
            countryCode: d.tenant?.country_code || '',
            phoneNumber: d.tenant?.phone_number || '',
            appliedDate: d.createdAt
              ? new Date(Number(d.createdAt)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : '—',
            propertyAddress: addr,
            propertyCode: prop.property_code || '—',
            grade: grade,
            ai_score: score,
            recommendation: {
              title: rec.title,
              description: rec.description,
              verifications: [
                { label: 'Document Authenticity', status: docStatus ? 'Verified' : 'Pending' },
                { label: 'Screening Grade', status: grade !== '—' ? grade : 'Pending' },
                ...(score != null ? [{ label: 'Tenant Score', status: `${score}` }] : []),
              ].filter(Boolean),
            },
            voiceScreening: { transcript },
            documents,
            references,
          });
          return {
            propertyId,
            tenantDocumentId,
            approvals: Array.isArray(d.approvals) ? d.approvals : [],
          };
        } else if (!silent) {
          toast.error(response?.message || 'Failed to load screening details');
          navigate(`/landlord/properties/${id}`);
        }
        return null;
      } catch (error) {
        if (!silent) {
          console.error('Error fetching screening:', error);
          toast.error('Failed to load screening details.');
          navigate(`/landlord/properties/${id}`);
        }
        return null;
      } finally {
        if (!silent) setIsLoading(false);
      }
  }, [tenantId, id, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const meta = await fetchScreening(false);
      if (cancelled) return;
      if (meta?.propertyId && meta?.tenantDocumentId) {
        await fetchReferenceCheckStatus(meta.propertyId, meta.tenantDocumentId, { silent: true });
      }
      if (cancelled || !meta?.propertyId || !meta?.tenantDocumentId) return;
      if (!shouldFetchCleardilVerification(meta.approvals)) return;
      await fetchVerificationResult(meta.propertyId, meta.tenantDocumentId, { silent: true });
      if (!cancelled) await fetchScreening(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchScreening, fetchVerificationResult, fetchReferenceCheckStatus]);

  const handleVerifyReference = async () => {
    const propertyId = tenant?.propertyId;
    const tenantDocumentId = tenant?.tenantDocumentId ?? tenant?.documents?.[0]?.tenant_document_id;
    if (!propertyId || !tenantDocumentId) {
      toast.error('Missing property or document information for reference verification.');
      return;
    }
    if (!canSendReferenceCheck(referenceCheck)) {
      toast.info('Reference verification has already been sent.');
      return;
    }
    setIsVerifyingReference(true);
    try {
      const response = await postApi('/reference-check/send', {
        property_id: propertyId,
        tenant_document_id: tenantDocumentId,
      });
      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Reference verification SMS sent.');
        await fetchReferenceCheckStatus(propertyId, tenantDocumentId, { silent: true });
      } else {
        toast.error(response?.message || 'Failed to send reference verification.');
      }
    } catch (error) {
      console.error('Error sending reference check:', error);
      toast.error(error?.response?.data?.message || 'Failed to send reference verification. Please try again.');
    } finally {
      setIsVerifyingReference(false);
    }
  };

  const openBackgroundCheckModal = () => {
    setBackgroundCheckPackage(CHECKR_PACKAGE.ESSENTIAL);
    setBackgroundCheckModalOpen(true);
  };
  const closeBackgroundCheckModal = () => {
    if (isRequestingBackgroundCheck) return;
    setBackgroundCheckModalOpen(false);
  };

  const handleRequestBackgroundCheck = async () => {
    const propertyId = tenant?.propertyId;
    const tenantUserId = tenant?.tenantUserId;
    if (!propertyId || !tenantUserId) {
      toast.error('Missing property or tenant information for background check.');
      return;
    }
    setIsRequestingBackgroundCheck(true);
    try {
      const response = await postApi('/verification/tenant/checkr/request-background-check', {
        property_id: propertyId,
        tenant_id: tenantUserId,
        package: backgroundCheckPackage,
      });
      if (response?.statusCode === 200) {
        setBackgroundCheckModalOpen(false);
        toast.success(
          response?.data?.reused
            ? 'The tenant already has a recent background check. It has been shared with you.'
            : response?.message || 'Background check requested. The tenant has been notified to complete it.'
        );
        await fetchScreening(true);
      } else {
        toast.error(response?.message || 'Failed to request background check.');
      }
    } catch (error) {
      console.error('Error requesting background check:', error);
      toast.error(error?.response?.data?.message || 'Failed to request background check. Please try again.');
    } finally {
      setIsRequestingBackgroundCheck(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = (firstName || '')[0] || '';
    const last = (lastName || '')[0] || '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || '?';
  };

  const handleApprove = () => {
    submitDecision(1);
  };

  const handleDecline = () => {
    openDeclineModal();
  };

  const handleAutoScreening = async () => {
    const propertyId = tenant?.propertyId;
    const tenantDocumentId = tenant?.documents?.[0]?.tenant_document_id;
    if (!propertyId || !tenantDocumentId) {
      toast.error('Missing property or document information for verification.');
      return;
    }
    setIsAutoScreening(true);
    try {
      const response = await postApi('/verification/tenant/start-verification', {
        property_id: propertyId,
        tenant_document_id: tenantDocumentId,
      });
      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Verification started.');
        const meta = await fetchScreening(true);
        if (
          meta &&
          shouldFetchCleardilVerification(meta.approvals) &&
          meta.propertyId &&
          meta.tenantDocumentId
        ) {
          await fetchVerificationResult(meta.propertyId, meta.tenantDocumentId, { silent: false });
          await fetchScreening(true);
        }
      } else {
        toast.error(response?.message || 'Failed to start verification.');
      }
    } catch (error) {
      console.error('Error starting verification:', error);
      toast.error(error?.response?.data?.message || 'Failed to start verification. Please try again.');
    } finally {
      setIsAutoScreening(false);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setDocumentViewIndex(0);
    setIsDocumentModalOpen(true);
  };

  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
    setDocumentViewIndex(0);
  };

  const documentUrls = selectedDocument?.allUrls && selectedDocument.allUrls.length > 0
    ? selectedDocument.allUrls
    : selectedDocument?.imageUrl
      ? [selectedDocument.imageUrl]
      : [];
  const currentDocumentUrl = documentUrls[documentViewIndex] || null;
  const currentIsPdf = currentDocumentUrl ? /\.pdf$/i.test(currentDocumentUrl) : false;
  const canGoPrev = documentUrls.length > 1 && documentViewIndex > 0;
  const canGoNext = documentUrls.length > 1 && documentViewIndex < documentUrls.length - 1;

  const handlePrevDocument = () => {
    if (canGoPrev) setDocumentViewIndex((i) => i - 1);
  };

  const handleNextDocument = () => {
    if (canGoNext) setDocumentViewIndex((i) => i + 1);
  };

  const handleDownloadDocument = () => {
    const url = currentDocumentUrl || selectedDocument?.imageUrl;
    if (!url) return;
    const isPdf = currentIsPdf || selectedDocument?.isPdf || /\.pdf$/i.test(url);
    const ext = isPdf ? 'pdf' : 'jpg';
    const baseName = (selectedDocument?.type || 'document').replace(/\s+/g, '_');
    const filename = documentUrls.length > 1
      ? `${baseName}_${documentViewIndex + 1}.${ext}`
      : `${baseName}.${ext}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openDeclineModal = (doc = null) => {
    setDeclineContext(doc ? { type: 'document', doc } : { type: 'application' });
    setDeclineModalOpen(true);
  };
  const closeDeclineModal = () => {
    setDeclineModalOpen(false);
    setDeclineReason('');
    setDeclineReasonError('');
    setDeclineContext(null);
  };

  const submitDecision = async (property_tenant_status, landlord_comment = '') => {
    if (!tenantId) return;
    setIsSubmittingDecision(true);
    try {
      const response = await postApi(`/screening/${tenantId}/decision`, {
        property_tenant_status,
        landlord_comment: landlord_comment.trim() || undefined,
      });
      if (response?.statusCode === 200) {
        toast.success(property_tenant_status === 1 ? 'Application approved.' : 'Application declined.');
        if (property_tenant_status === 2) closeDeclineModal();
        navigate(`/landlord/properties/${id}`);
      } else {
        toast.error(response?.message || 'Failed to update decision.');
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      toast.error('Failed to update decision. Please try again.');
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  const submitReviewDocument = async (doc, status, comment = '') => {
    if (!tenant?.propertyId || doc?.tenant_document_id == null) return;
    const field_name = doc.field_name;
    if (field_name == null) return;
    setIsSubmittingDecision(true);
    try {
      const response = await postApi('/screening/review-document', {
        property_id: tenant.propertyId,
        tenant_document_id: doc.tenant_document_id,
        field_name: Number(field_name),
        status,
        comment: comment.trim() || undefined,
      });
      if (response?.statusCode === 200) {
        toast.success(status === 1 ? 'Document approved.' : 'Document declined.');
        closeDeclineModal();
        fetchScreening(true);
      } else {
        toast.error(response?.message || 'Failed to update document review.');
      }
    } catch (error) {
      console.error('Error reviewing document:', error);
      toast.error('Failed to update document review. Please try again.');
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  const handleDeclineSubmit = () => {
    const reason = (declineReason || '').trim();
    if (!reason) {
      setDeclineReasonError(declineContext?.type === 'document'
        ? 'Please provide a reason for declining this document.'
        : 'Please provide a reason for declining the application.');
      return;
    }
    setDeclineReasonError('');
    if (declineContext?.type === 'document' && declineContext.doc) {
      submitReviewDocument(declineContext.doc, 2, reason);
    } else {
      submitDecision(2, reason);
    }
  };

  const handleApproveDocument = (doc) => {
    if (doc?.tenant_document_id != null && doc?.field_name != null) {
      submitReviewDocument(doc, 1);
    }
  };

  // Grade badge colors: 1 = light green, 2 = light blue, 3 = light red, other = neutral
  const getGradeStyle = (grade) => {
    const g = grade != null && grade !== '' ? String(grade).trim() : '';
    if (g === '1') return { backgroundColor: '#d4edda', color: '#155724' };
    if (g === '2') return { backgroundColor: '#ABF0FD', color: '#004085' };
    if (g === '3') return { backgroundColor: '#f8d7da', color: '#721c24' };
    return { backgroundColor: '#e9ecef', color: '#495057' };
  };

  if (isLoading || !tenant) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
        Loading screening details...
      </div>
    );
  }

  // Auto-Screening: enabled only when BOTH documents are pending (none approved/declined)
  const allDocsPending =
    (tenant.documents?.length ?? 0) > 0 &&
    tenant.documents.every((d) => d.reviewStatus !== 'approved' && d.reviewStatus !== 'declined');
  const autoScreeningDisabled = !allDocsPending;
  const autoScreeningDisabledTitle = autoScreeningDisabled
    ? 'Auto-Screening is only available when both documents (Pay Stubs and Bank Statements) are in Pending state. Please ensure neither document has been approved or declined yet.'
    : '';

  // Approve Tenant: enabled only when BOTH documents are approved
  const bothDocsApproved =
    (tenant.documents?.length ?? 0) > 0 &&
    tenant.documents.every((d) => d.reviewStatus === 'approved');
  const approveTenantDisabled = !bothDocsApproved;
  const approveTenantDisabledTitle = approveTenantDisabled
    ? 'You can approve the tenant only after both documents (Pay Stubs and Bank Statements) have been approved. Please approve each document first.'
    : '';

  // Decline Application: enabled only when BOTH documents are reviewed (approved or declined)
  const bothDocsReviewed =
    (tenant.documents?.length ?? 0) > 0 &&
    tenant.documents.every((d) => d.reviewStatus === 'approved' || d.reviewStatus === 'declined');
  const declineDisabled = !bothDocsReviewed;
  const declineDisabledTitle = declineDisabled
    ? 'You can decline the application only after both documents (Pay Stubs and Bank Statements) have been reviewed. Please approve or decline each document first.'
    : '';

  const propertyTenantStatus = tenant?.propertyTenantStatus != null ? String(tenant.propertyTenantStatus) : '0';
  const isPending = propertyTenantStatus === '0';
  const isApproved = propertyTenantStatus === '1';
  const isDeclined = propertyTenantStatus === '2';
  // Property already assigned: no approve/decline decision can be taken any more
  const isAssignedToOther = tenant?.isPropertyAssigned === true;
  const assignedToOtherTitle = 'This property has already been assigned to a tenant, so this application can no longer be approved or declined.';
  const showAutoScreeningAndApprove = isPending && !isAssignedToOther;
  const showDeclineButton = (isPending || isApproved) && !isAssignedToOther;
  // No background check yet (status null) or the last one was canceled: landlord can request one from the tenant
  const showBackgroundCheckButton = canStartBackgroundCheck(tenant?.backgroundCheckStatus) && !isDeclined && !isAssignedToOther;
  const isCheckrReportReady = tenant?.backgroundCheckStatus === CHECKR_STATUS.COMPLETED;
  const referenceCheckDetails = referenceCheck ? buildReferenceCheckDetails(referenceCheck) : null;

  return (
    <div className="tenant-detail-page">
      <div className="screenings-actions-bar" style={{ marginBottom: '20px' }}>
        <button
          type="button"
          className="screenings-back-button"
          onClick={() => navigate(`/landlord/properties/${id}`)}
          aria-label="Go back"
        >
          <span className="screenings-back-button-icon" aria-hidden="true">
            ←
          </span>
        </button>
      </div>
      <div className="tenant-detail-container">
        <div className="tenant-detail-left">
          {/* Applicant Information Card */}
          <div className="tenant-detail-applicant-card">
            <div className="tenant-detail-applicant-header">
              <div className="tenant-detail-applicant-avatar">
                {getInitials(tenant.firstName, tenant.lastName)}
              </div>
              <div className="tenant-detail-applicant-info">
                <h2 className="tenant-detail-applicant-name">
                  {tenant.firstName}, {tenant.lastName}
                </h2>
                <div className="tenant-detail-applicant-property">
                  Applied for {tenant.propertyAddress}
                </div>
                <div className="tenant-detail-applicant-code">
                  Code: {tenant.propertyCode}
                </div>
                <div className="tenant-detail-applicant-contact">
                  <div className="tenant-detail-contact-item">
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl("/assets/images/email.png") : "/assets/images/email.png"} 
                      alt="Email" 
                      className="tenant-detail-contact-icon"
                    />
                    <span>{tenant.email}</span>
                  </div>
                  <div className="tenant-detail-contact-item">
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl("/assets/images/phone.png") : "/assets/images/phone.png"} 
                      alt="Phone" 
                      className="tenant-detail-contact-icon"
                    />
                    <FormattedPhone
                      countryCode={tenant.countryCode}
                      phoneNumber={tenant.phoneNumber}
                    />
                  </div>
                  <div className="tenant-detail-contact-item">
                    <span>Applied</span>
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl("/assets/images/calendar-01.png") : "/assets/images/calendar-01.png"} 
                      alt="Calendar" 
                      className="tenant-detail-contact-icon"
                    />
                    <span>{tenant.appliedDate}</span>
                  </div>
                </div>
                <div className="tenant-detail-background-check">
                  <span className="tenant-detail-background-check-label">Background Check</span>
                  <span className={`background-check-status background-check-status-${tenant.backgroundCheckStatus ?? 'none'}`}>
                    {getCheckrStatusLabel(tenant.backgroundCheckStatus)}
                  </span>
                  {isCheckrReportReady && (
                    <button
                      type="button"
                      className="tenant-detail-background-check-btn"
                      onClick={() =>
                        navigate(`/landlord/properties/${id}/tenant/${tenantId}/background-check`, {
                          state: {
                            propertyId: tenant.propertyId,
                            tenantUserId: tenant.tenantUserId,
                            tenantName: `${tenant.firstName} ${tenant.lastName}`.trim(),
                            backgroundCheckPackage: tenant.backgroundCheckPackage,
                          },
                        })
                      }
                    >
                      View
                    </button>
                  )}
                  {showBackgroundCheckButton && (
                    <button
                      type="button"
                      className="tenant-detail-background-check-btn"
                      onClick={openBackgroundCheckModal}
                      disabled={isRequestingBackgroundCheck}
                    >
                      {isRequestingBackgroundCheck ? 'Requesting…' : 'Request Background Check'}
                    </button>
                  )}
                </div>
              </div>
              <div className="tenant-detail-grade-badge" style={getGradeStyle(tenant.grade)}>
                {tenant.grade}
              </div>
            </div>
          </div>

          {/* Automation Voice Screening Summary */}
          <div className="tenant-detail-card">
            <div className="tenant-detail-voice-header">
              <img 
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/phone.png") : "/assets/images/phone.png"} 
                alt="Phone" 
                className="tenant-detail-voice-icon"
              />
              <h3 className="tenant-detail-card-title">Automation Screening Summary</h3>
            </div>
            <div className="tenant-detail-voice-transcript-wrapper">
              <div className="tenant-detail-voice-transcript">
                {tenant.voiceScreening?.transcript?.length > 0 ? (
                  tenant.voiceScreening.transcript.map((item, index) => (
                    <div key={index} className="tenant-detail-voice-message">
                      <span className="tenant-detail-voice-speaker">{item.speaker}:</span>
                      <span className="tenant-detail-voice-text">{item.message}</span>
                    </div>
                  ))
                ) : (
                  <p className="tenant-detail-voice-text" style={{ color: '#666' }}>No transcript available.</p>
                )}
              </div>
              {tenant.voiceScreening?.transcript?.length > 4 && (
                <div className="tenant-detail-voice-transcript-fade" aria-hidden="true" />
              )}
            </div>
          </div>

          {/* Document Verification */}
          <div className="tenant-detail-card">
            <div className="tenant-detail-card-header">
              <img 
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/file-04.png") : "/assets/images/file-04.png"} 
                alt="Document" 
                className="tenant-detail-card-icon"
              />
              <h3 className="tenant-detail-card-title">Document Verification</h3>
            </div>
            <div className="tenant-detail-documents-list">
              {tenant.documents?.length > 0 ? (
                tenant.documents.map((doc, index) => (
                  <div key={index} className="tenant-detail-document-item">
                    <div className="tenant-detail-document-info">
                      <div className="tenant-detail-document-type">{doc.type}</div>
                      {doc.period ? <div className="tenant-detail-document-period">{doc.period}</div> : null}
                    </div>
                    <div className="tenant-detail-document-actions">
                      <button 
                        className="tenant-detail-action-icon"
                        onClick={() => handleViewDocument(doc)}
                        disabled={!doc.imageUrl}
                      >
                        <img 
                          src={window.getPublicUrl ? window.getPublicUrl("/assets/images/eye-open_new.png") : "/assets/images/eye-open_new.png"} 
                          alt="View" 
                        />
                      </button>
                      {doc.reviewStatus === 'approved' ? (
                        <span className="tenant-detail-document-approved">Approved</span>
                      ) : doc.reviewStatus === 'declined' ? (
                        <span className="tenant-detail-document-declined">Declined</span>
                      ) : (
                        <span className="tenant-detail-verified-badge">{doc.verified ? 'Verified' : 'Pending'}</span>
                      )}
                      {isAssignedToOther ? null : doc.reviewStatus === 'approved' ? (
                        <button 
                          className="tenant-detail-decline-btn"
                          onClick={() => openDeclineModal(doc)}
                          disabled={isSubmittingDecision || doc.tenant_document_id == null}
                        >
                          Decline
                        </button>
                      ) : doc.reviewStatus === 'declined' ? (
                        <button 
                          className="tenant-detail-approve-btn" 
                          onClick={() => handleApproveDocument(doc)}
                          disabled={isSubmittingDecision || doc.tenant_document_id == null}
                        >
                          Approve
                        </button>
                      ) : (
                        <>
                          <button 
                            className="tenant-detail-approve-btn" 
                            onClick={() => handleApproveDocument(doc)}
                            disabled={isSubmittingDecision || doc.tenant_document_id == null}
                          >
                            Approve
                          </button>
                          <button 
                            className="tenant-detail-decline-btn"
                            onClick={() => openDeclineModal(doc)}
                            disabled={isSubmittingDecision || doc.tenant_document_id == null}
                          >
                            Decline
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ color: '#666', margin: 0 }}>No documents submitted.</p>
              )}
            </div>
          </div>

          {/* Reference Verification */}
          <div className="tenant-detail-card">
            <div className="tenant-detail-card-header">
              <img 
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/users-applicants.png") : "/assets/images/users-applicants.png"} 
                alt="Reference" 
                className="tenant-detail-card-icon"
              />
              <h3 className="tenant-detail-card-title">Reference Verification</h3>
            </div>
            <div className="tenant-detail-references-list">
              {tenant.references?.length > 0 ? (
                tenant.references.map((ref, index) => (
                  <div key={index} className="tenant-detail-reference-item">
                    <div className="tenant-detail-reference-info">
                      <div className="tenant-detail-reference-name">{ref.name}</div>
                      {ref.address ? <div className="tenant-detail-reference-address">{ref.address}</div> : null}
                    </div>
                    {(ref.email || ref.phone) ? (
                      <div className="tenant-detail-reference-contact">
                        <div className="tenant-detail-reference-contact-line">
                          <span className="tenant-detail-reference-contact-label">Contact:-</span>
                          {ref.email ? <span className="tenant-detail-reference-email">{ref.email}</span> : null}
                        </div>
                        {ref.phone ? (
                          <div className="tenant-detail-reference-phone">
                            <FormattedPhone phone={ref.phone} />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <p className="tenant-detail-reference-empty">No references provided.</p>
              )}
              <div className="tenant-detail-reference-verify">
                <span className="tenant-detail-reference-verify-label">Verify Tenant</span>
                <button
                  type="button"
                  className="tenant-detail-reference-verify-btn"
                  onClick={handleVerifyReference}
                  disabled={
                    isVerifyingReference ||
                    isReferenceCheckLoading ||
                    !canSendReferenceCheck(referenceCheck)
                  }
                >
                  {isVerifyingReference ? 'Sending…' : 'Verify'}
                </button>
              </div>
              {isReferenceCheckLoading && !referenceCheck ? (
                <p className="tenant-detail-reference-check-loading">Loading verification status…</p>
              ) : null}
              {referenceCheckDetails ? (
                <div className="tenant-detail-reference-check-status">
                  <div className="tenant-detail-reference-check-header">
                    <h4 className="tenant-detail-reference-check-title">Verification details</h4>
                    <span
                      className={`tenant-detail-reference-check-badge tenant-detail-reference-check-badge--${referenceCheckDetails.statusInfo.variant}`}
                    >
                      {referenceCheckDetails.statusInfo.label}
                    </span>
                  </div>
                  {referenceCheckDetails.rows.length > 0 ? (
                    <dl className="tenant-detail-reference-check-grid">
                      {referenceCheckDetails.rows.map((row) => (
                        <div key={row.id} className="tenant-detail-reference-check-grid-item">
                          <dt className="tenant-detail-reference-check-label">{row.label}</dt>
                          <dd className="tenant-detail-reference-check-value">
                            {row.isPhone ? <FormattedPhone phone={row.value} /> : row.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {referenceCheckDetails.smsResponse ? (
                    <div className="tenant-detail-reference-check-response">
                      <div className="tenant-detail-reference-check-response-label">Landlord response</div>
                      <p className="tenant-detail-reference-check-response-text">{referenceCheckDetails.smsResponse}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {/* Lease Agreement — available once the tenant has been approved */}
          {isApproved && (
            <LeaseAgreementCard
              propertyId={tenant.propertyId}
              tenantId={tenant.tenantUserId}
              tenantName={[tenant.firstName, tenant.lastName].filter(Boolean).join(' ')}
              onViewDocument={handleViewDocument}
            />
          )}
        </div>

        {/* Candidate Recommendation Card */}
        <div className="tenant-detail-right">
          <div className="tenant-detail-recommendation-card">
            <div className="tenant-detail-recommendation-grade" style={getGradeStyle(tenant.grade)}>
              {tenant.grade}
            </div>
            <h3 className="tenant-detail-recommendation-title">
              {tenant.recommendation.title}
            </h3>
            <p className="tenant-detail-recommendation-description">
              {tenant.recommendation.description}
            </p>
            <div className="tenant-detail-verifications">
              {tenant.recommendation.verifications.map((verification, index) => (
                <div key={index} className="tenant-detail-verification-item">
                  <div className="tenant-detail-verification-header">
                    <span className="tenant-detail-verification-label">{verification.label}</span>
                    <span className="tenant-detail-verification-status">{verification.status}</span>
                  </div>
                  <div className="tenant-detail-verification-progress">
                    <div className="tenant-detail-verification-progress-bar"></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="tenant-detail-actions">
              {isAssignedToOther && (
                <div className="tenant-detail-assigned-notice" role="note">
                  {assignedToOtherTitle}
                </div>
              )}
              {showAutoScreeningAndApprove && (
                <div className={`tenant-detail-action-wrap${autoScreeningDisabled ? ' tenant-detail-action-wrap--has-tooltip' : ''}`}>
                  <button 
                    className="tenant-detail-action-btn tenant-detail-action-auto"
                    onClick={handleAutoScreening}
                    disabled={autoScreeningDisabled || isAutoScreening}
                    aria-label={autoScreeningDisabled ? autoScreeningDisabledTitle : 'Run auto-screening'}
                  >
                    {isAutoScreening ? 'Starting…' : 'Auto-Screening'}
                  </button>
                  {autoScreeningDisabled && autoScreeningDisabledTitle && (
                    <div className="tenant-detail-action-tooltip" role="tooltip">
                      {autoScreeningDisabledTitle}
                    </div>
                  )}
                </div>
              )}
              {showAutoScreeningAndApprove && (
                <div className={`tenant-detail-action-wrap${approveTenantDisabled ? ' tenant-detail-action-wrap--has-tooltip' : ''}`}>
                  <button 
                    className="tenant-detail-action-btn tenant-detail-action-approve"
                    onClick={handleApprove}
                    disabled={isSubmittingDecision || approveTenantDisabled}
                    aria-label={approveTenantDisabled ? approveTenantDisabledTitle : 'Approve tenant'}
                  >
                    Approve Tenant
                  </button>
                  {approveTenantDisabled && approveTenantDisabledTitle && (
                    <div className="tenant-detail-action-tooltip" role="tooltip">
                      {approveTenantDisabledTitle}
                    </div>
                  )}
                </div>
              )}
              {showDeclineButton && (
                <div className={`tenant-detail-action-wrap${showAutoScreeningAndApprove && declineDisabled ? ' tenant-detail-action-wrap--has-tooltip' : ''}`}>
                  <button 
                    className="tenant-detail-action-btn tenant-detail-action-decline"
                    onClick={handleDecline}
                    disabled={isSubmittingDecision || (isPending && declineDisabled)}
                    aria-label={isPending && declineDisabled ? declineDisabledTitle : 'Decline application'}
                  >
                    Decline Application
                  </button>
                  {showAutoScreeningAndApprove && declineDisabled && declineDisabledTitle && (
                    <div className="tenant-detail-action-tooltip" role="tooltip">
                      {declineDisabledTitle}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Decline Application Modal */}
      <Modal
        isOpen={declineModalOpen}
        onRequestClose={closeDeclineModal}
        contentLabel="Decline Application"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeDeclineModal} type="button" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h2 className="logout-modal-title">
          {declineContext?.type === 'document' ? 'Decline document' : 'Decline Application'}
        </h2>
        <p className="logout-modal-message">
          {declineContext?.type === 'document'
            ? 'Please provide a reason for declining this document.'
            : 'Please provide a reason for declining this application.'}
        </p>
        <textarea
          className={`logout-modal-textarea${declineReasonError ? ' logout-modal-textarea--error' : ''}`}
          value={declineReason}
          onChange={(e) => {
            setDeclineReason(e.target.value);
            if (declineReasonError) setDeclineReasonError('');
          }}
          placeholder={declineContext?.type === 'document' ? 'Enter reason for document decline...' : 'Enter reason for decline...'}
          rows={4}
          aria-label="Reason for decline"
          aria-invalid={!!declineReasonError}
          aria-describedby={declineReasonError ? 'decline-reason-error' : undefined}
        />
        {declineReasonError && (
          <p id="decline-reason-error" className="logout-modal-field-error" role="alert">
            {declineReasonError}
          </p>
        )}
        <div className="logout-modal-buttons">
          <button type="button" className="logout-modal-cancel" onClick={closeDeclineModal}>Cancel</button>
          <button 
            type="button" 
            className="logout-modal-confirm" 
            onClick={handleDeclineSubmit}
            disabled={isSubmittingDecision}
            title={!(declineReason || '').trim() ? 'Please enter a reason before declining.' : ''}
          >
            {isSubmittingDecision ? 'Declining...' : 'Decline'}
          </button>
        </div>
      </Modal>

      {/* Background Check Package Modal */}
      <Modal
        isOpen={backgroundCheckModalOpen}
        onRequestClose={closeBackgroundCheckModal}
        contentLabel="Request Background Check"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal background-check-package-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeBackgroundCheckModal} type="button" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h2 className="logout-modal-title">Request Background Check</h2>
        <p className="logout-modal-message">Choose the background check package for this tenant.</p>
        <div className="background-check-package-options" role="radiogroup" aria-label="Background check package">
          {CHECKR_PACKAGE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`background-check-package-option${backgroundCheckPackage === option.value ? ' background-check-package-option--selected' : ''}`}
            >
              <input
                type="radio"
                name="background-check-package"
                value={option.value}
                checked={backgroundCheckPackage === option.value}
                onChange={() => setBackgroundCheckPackage(option.value)}
                disabled={isRequestingBackgroundCheck}
              />
              <span className="background-check-package-option-body">
                <span className="background-check-package-option-header">
                  <span className="background-check-package-option-title">{option.title}</span>
                  <span className="background-check-package-option-price">{option.price}</span>
                </span>
                <ul className="background-check-package-option-features">
                  {option.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </span>
            </label>
          ))}
        </div>
        <div className="logout-modal-buttons">
          <button type="button" className="logout-modal-cancel" onClick={closeBackgroundCheckModal} disabled={isRequestingBackgroundCheck}>
            Cancel
          </button>
          <button
            type="button"
            className="logout-modal-confirm background-check-package-confirm"
            onClick={handleRequestBackgroundCheck}
            disabled={isRequestingBackgroundCheck}
          >
            {isRequestingBackgroundCheck ? 'Requesting…' : 'Request'}
          </button>
        </div>
      </Modal>

      {/* Document View Modal */}
      <Modal
        isOpen={isDocumentModalOpen}
        onRequestClose={handleCloseDocumentModal}
        contentLabel="Document View Modal"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="document-view-modal"
        overlayClassName="document-view-modal-overlay"
      >
        <div className="document-view-modal-header">
          <h2 className="document-view-modal-title">
            {selectedDocument?.type || 'Document'}
          </h2>
          <div className="document-view-modal-header-actions">
            <button 
              className="document-view-modal-download"
              onClick={handleDownloadDocument}
              title="Download"
            >
              <img 
                src={window.getPublicUrl ? window.getPublicUrl("/assets/images/downloadBtn.svg") : "/assets/images/downloadBtn.svg"} 
                alt="Download" 
              />
            </button>
            <button 
              className="document-view-modal-close"
              onClick={handleCloseDocumentModal}
              title="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="document-view-modal-content">
          {selectedDocument && currentDocumentUrl && (
            currentIsPdf ? (
              <iframe
                key={documentViewIndex}
                src={currentDocumentUrl}
                title={`${selectedDocument.type} ${documentViewIndex + 1}`}
                className="document-view-modal-image"
                style={{ width: '100%', minHeight: '70vh', border: 'none' }}
              />
            ) : (
              <img
                key={documentViewIndex}
                src={currentDocumentUrl}
                alt={`${selectedDocument.type} ${documentViewIndex + 1}`}
                className="document-view-modal-image"
              />
            )
          )}
          {documentUrls.length > 1 && (
            <div className="document-view-modal-nav">
              <button
                type="button"
                className="document-view-modal-nav-btn document-view-modal-prev"
                onClick={handlePrevDocument}
                disabled={!canGoPrev}
                aria-label="Previous document"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className="document-view-modal-counter">
                {documentViewIndex + 1} / {documentUrls.length}
              </span>
              <button
                type="button"
                className="document-view-modal-nav-btn document-view-modal-next"
                onClick={handleNextDocument}
                disabled={!canGoNext}
                aria-label="Next document"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TenantDetail;
